import { type JSX, type MouseEvent, type ReactNode } from 'react';

import { routes } from '../routes';
import { AppLink, useOptionalNavigator } from '../discovery/router';
import { rememberReturnPath } from './preferences';

/**
 * The pieces every website page shares, drawn from the page frames' own markup
 * (`Just Treated Account / Orders / Bookings / Rewards / Prescriptions`): the
 * Back link, the 640px logged-out panel, and — for a page whose signed-in view
 * is not built yet — a panel in the same style that says so plainly.
 */

/** The frames' "Back" pill: back in history when the visitor came from this site, else home. */
export function BackLink({ label = 'Back' }: { readonly label?: string }): JSX.Element {
  const navigator = useOptionalNavigator();

  const onClick = (event: MouseEvent<HTMLAnchorElement>): void => {
    const referrer = document.referrer;
    let sameSite = false;
    try {
      sameSite = referrer !== '' && new URL(referrer).origin === window.location.origin;
    } catch {
      sameSite = false;
    }
    if (sameSite && window.history.length > 1) {
      event.preventDefault();
      window.history.back();
      return;
    }
    if (navigator !== undefined) {
      event.preventDefault();
      navigator.navigate(routes.home.path);
    }
  };

  return (
    <a href={routes.home.path} className="jt-site-back" onClick={onClick}>
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M14.6 5.4 8 12l6.6 6.6" />
      </svg>
      {label}
    </a>
  );
}

/** The padlock the frames draw in the panel's corner. */
function LockTile(): JSX.Element {
  return (
    <span className="jt-site-card__icon" aria-hidden="true">
      <svg
        width="26"
        height="26"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#fff"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M5 11h14v10H5zM8 11V8a4 4 0 0 1 7.5-2" />
        <path d="M12 15v2" />
      </svg>
    </span>
  );
}

/** A link to the Log In page that remembers where the visitor was. */
export function LoginLink({
  className,
  children,
}: {
  readonly className: string;
  readonly children: ReactNode;
}): JSX.Element {
  return (
    <span className="jt-site-contents" onClickCapture={() => rememberReturnPath()}>
      <AppLink to={routes.logIn.path} className={className}>
        {children}
      </AppLink>
    </span>
  );
}

export interface GateCopy {
  readonly head: string;
  readonly copy: string;
}

/**
 * The frame's logged-out panel: the page title, a lavender card with what
 * logging in gives you, and Create an account / Log in. Both go to the one
 * Log In page, which signs in or creates the account, and both remember the
 * page so the visitor comes back to it.
 */
export function SignInGate({
  title,
  subtitle,
  gate,
}: {
  readonly title: string;
  readonly subtitle?: string;
  readonly gate: GateCopy;
}): JSX.Element {
  return (
    <div className="jt-site-gate">
      <PageTitle title={title} subtitle={subtitle} />
      <div className="jt-site-card">
        <div className="jt-site-card__row">
          <span className="jt-site-card__text">
            <span className="jt-site-card__head">{gate.head}</span>
            <span className="jt-site-card__copy">{gate.copy}</span>
          </span>
          <LockTile />
        </div>
        <div className="jt-site-card__actions">
          <LoginLink className="jt-site-pill jt-site-pill--white">Create an account</LoginLink>
          <LoginLink className="jt-site-pill jt-site-pill--ink">Log in</LoginLink>
        </div>
      </div>
    </div>
  );
}

function PageTitle({ title, subtitle }: { readonly title: string; readonly subtitle?: string }): JSX.Element {
  if (subtitle === undefined) return <h1 className="jt-site-h1">{title}</h1>;
  return (
    <div className="jt-site-titleblock">
      <h1 className="jt-site-h1">{title}</h1>
      <span className="jt-site-subtitle">{subtitle}</span>
    </div>
  );
}

/**
 * A page that is not built yet, said plainly in the frames' panel style.
 *
 * Deliberately not a mock of the finished page: no sample orders, no invented
 * balance, no demo person. When the page's stage ships, this is replaced.
 */
export function ComingSoonPanel({
  title,
  subtitle,
  copy,
}: {
  readonly title: string;
  readonly subtitle?: string;
  readonly copy: string;
}): JSX.Element {
  return (
    <div className="jt-site-gate">
      <PageTitle title={title} subtitle={subtitle} />
      <div className="jt-site-card" role="status">
        <div className="jt-site-card__row">
          <span className="jt-site-card__text">
            <span className="jt-site-card__head">This page is being built</span>
            <span className="jt-site-card__copy">{copy}</span>
          </span>
          <span className="jt-site-card__icon" aria-hidden="true">
            <svg
              width="26"
              height="26"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#fff"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="8.5" />
              <path d="M12 7.5V12l3 2" />
            </svg>
          </span>
        </div>
        <div className="jt-site-card__actions">
          <AppLink to={routes.home.path} className="jt-site-pill jt-site-pill--ink">
            Go to the homepage
          </AppLink>
        </div>
      </div>
    </div>
  );
}

/** While the session is being checked: the title, and nothing that could be mistaken for content. */
export function CheckingPanel({ title }: { readonly title: string }): JSX.Element {
  return (
    <div className="jt-site-gate" aria-busy="true">
      <h1 className="jt-site-h1">{title}</h1>
      <div className="jt-site-card jt-site-card--quiet">
        <span className="jt-site-card__copy">Checking whether you’re logged in…</span>
      </div>
    </div>
  );
}

/** The session could not be checked at all (network, server down). Not the same as logged out. */
export function UnreachablePanel({
  title,
  onRetry,
}: {
  readonly title: string;
  readonly onRetry: () => void;
}): JSX.Element {
  return (
    <div className="jt-site-gate">
      <h1 className="jt-site-h1">{title}</h1>
      <div className="jt-site-card" role="alert">
        <span className="jt-site-card__text">
          <span className="jt-site-card__head">We couldn’t reach Just Treated</span>
          <span className="jt-site-card__copy">Check your connection, then try again.</span>
        </span>
        <div className="jt-site-card__actions">
          <button type="button" className="jt-site-pill jt-site-pill--ink" onClick={onRetry}>
            Try again
          </button>
        </div>
      </div>
    </div>
  );
}
